// CSV bulk-import / export for the teacher dashboard.
//
// Format (RFC 4180 — comma separator, double-quote escape, CRLF line
// endings, single header row):
//
//   lesson_id,lesson_title,theme,time_of_day,word_id,english,vietnamese,sentence,image_url,order
//
// One row per word. Lesson metadata (title/theme/time) repeats per
// row — flat structure makes the file trivially editable in Excel,
// Google Sheets, Numbers, etc. New lessons can be created on import
// just by inventing a lesson_id and using it on every row of the new
// pack.
//
// Round-trip safety: importing the output of an export reproduces the
// same lessons (allowing for save-time `updatedAt` changes).

import type { ThemeId, TimeOfDay } from "@shared/types";
import {
  blankLesson,
  newWord,
  saveCustomLesson,
  listCustomLessons,
  type CustomLesson,
} from "./customLessons";

const HEADERS = [
  "lesson_id",
  "lesson_title",
  "theme",
  "time_of_day",
  "word_id",
  "english",
  "vietnamese",
  "sentence",
  "image_url",
  "order",
] as const;

/** RFC 4180 quoting — wrap any field containing comma, quote, CR or LF
 *  in double quotes, and double-escape embedded quotes. */
function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

/** Export every saved custom lesson to a single CSV string. */
export function lessonsToCsv(lessons: CustomLesson[]): string {
  const rows: string[] = [HEADERS.join(",")];
  for (const l of lessons) {
    if (l.words.length === 0) {
      // Still emit a row so the lesson metadata round-trips even when
      // it has no words yet. Word fields are blank.
      rows.push(
        [
          l.id,
          l.title,
          l.theme,
          l.timeOfDay,
          "",
          "",
          "",
          "",
          "",
          "",
        ].map(csvEscape).join(","),
      );
      continue;
    }
    for (let i = 0; i < l.words.length; i++) {
      const w = l.words[i]!;
      rows.push(
        [
          l.id,
          l.title,
          l.theme,
          l.timeOfDay,
          w.id,
          w.english,
          w.vietnamese,
          w.sentence,
          w.imageUrl ?? "",
          String(w.orderIndex ?? i),
        ].map(csvEscape).join(","),
      );
    }
  }
  return rows.join("\r\n") + "\r\n";
}

/** RFC 4180 parser — handles quoted fields, embedded quotes, CR/LF in
 *  data, and either CRLF / LF / CR line endings. */
function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (i + 1 < n && text[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        field += c;
        i++;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
        i++;
      } else if (c === ",") {
        cur.push(field);
        field = "";
        i++;
      } else if (c === "\r" || c === "\n") {
        cur.push(field);
        field = "";
        rows.push(cur);
        cur = [];
        // Skip the LF half of CRLF.
        if (c === "\r" && i + 1 < n && text[i + 1] === "\n") i += 2;
        else i++;
      } else {
        field += c;
        i++;
      }
    }
  }
  // Final field/row if file didn't end with a newline.
  if (field.length > 0 || cur.length > 0) {
    cur.push(field);
    rows.push(cur);
  }
  // Drop empty trailing rows (single empty field from a trailing blank line).
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

export interface CsvImportReport {
  /** Total lessons in the file after parsing. */
  lessonsParsed: number;
  /** Lessons that were created (new id) on import. */
  lessonsCreated: number;
  /** Lessons that updated an existing id. */
  lessonsUpdated: number;
  /** Total word rows imported. */
  wordsImported: number;
  /** Any non-fatal warnings (skipped rows, missing fields, etc.). */
  warnings: string[];
}

/**
 * Parse a CSV string and persist the lessons to localStorage via
 * `saveCustomLesson`. Returns a small report so the UI can show
 * "Imported N lessons / M words."
 */
export function importCsv(text: string): CsvImportReport {
  const rows = parseCsvRows(text);
  const report: CsvImportReport = {
    lessonsParsed: 0,
    lessonsCreated: 0,
    lessonsUpdated: 0,
    wordsImported: 0,
    warnings: [],
  };
  if (rows.length === 0) {
    report.warnings.push("CSV is empty.");
    return report;
  }
  // Header row — case-insensitive, accept either snake_case or the
  // common "English / Vietnamese / Sentence" plain-language headers.
  const header = rows[0]!.map((h) => h.trim().toLowerCase());
  const col = (...names: string[]): number => {
    for (const n of names) {
      const idx = header.indexOf(n);
      if (idx >= 0) return idx;
    }
    return -1;
  };
  const colLessonId = col("lesson_id", "lesson id");
  const colTitle    = col("lesson_title", "title", "pack", "pack title");
  const colTheme    = col("theme");
  const colTime     = col("time_of_day", "time");
  const colWordId   = col("word_id");
  const colEnglish  = col("english", "en", "word");
  const colVi       = col("vietnamese", "vi", "translation");
  const colSentence = col("sentence", "example", "example_sentence");
  const colImage    = col("image_url", "image");
  const colOrder    = col("order", "order_index", "orderindex");

  if (colEnglish < 0 || colVi < 0) {
    report.warnings.push("CSV must include at least `english` and `vietnamese` columns.");
    return report;
  }

  // Group rows by lesson_id (falling back to lesson_title if no id).
  const lessonBuckets = new Map<string, { meta: { id: string; title: string; theme: string; timeOfDay: string }; rows: typeof rows }>();
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r]!;
    const id = colLessonId >= 0 ? (row[colLessonId] ?? "").trim() : "";
    const title = colTitle >= 0 ? (row[colTitle] ?? "").trim() : "";
    if (!id && !title) {
      report.warnings.push(`Row ${r + 1}: no lesson_id or lesson_title — skipped.`);
      continue;
    }
    const key = id || `__by_title__${title}`;
    let bucket = lessonBuckets.get(key);
    if (!bucket) {
      bucket = {
        meta: {
          id: id || "",
          title,
          theme: (colTheme >= 0 ? (row[colTheme] ?? "").trim() : ""),
          timeOfDay: (colTime >= 0 ? (row[colTime] ?? "").trim() : ""),
        },
        rows: [],
      };
      lessonBuckets.set(key, bucket);
    }
    bucket.rows.push(row);
  }

  const existing = listCustomLessons();
  const existingById = new Map(existing.map((l) => [l.id, l] as const));
  const VALID_THEMES = new Set<ThemeId>(["candy", "forest", "jungle", "snow", "desert", "space"]);
  const VALID_TIMES  = new Set<TimeOfDay>(["auto", "morning", "afternoon", "sunset", "night"]);

  for (const [, bucket] of lessonBuckets) {
    report.lessonsParsed++;
    const prev = bucket.meta.id ? existingById.get(bucket.meta.id) ?? null : null;
    const base = prev ?? blankLesson();
    const theme = (VALID_THEMES.has(bucket.meta.theme as ThemeId) ? bucket.meta.theme : base.theme) as ThemeId;
    const timeOfDay = (VALID_TIMES.has(bucket.meta.timeOfDay as TimeOfDay) ? bucket.meta.timeOfDay : base.timeOfDay) as TimeOfDay;
    const words = bucket.rows
      .map((row, i) => {
        const en = (colEnglish >= 0 ? (row[colEnglish] ?? "").trim() : "");
        const vi = (colVi >= 0 ? (row[colVi] ?? "").trim() : "");
        if (!en && !vi) return null; // metadata-only row
        const w = newWord();
        if (colWordId >= 0) {
          const wid = (row[colWordId] ?? "").trim();
          if (wid) w.id = wid;
        }
        w.english = en;
        w.vietnamese = vi;
        w.sentence = colSentence >= 0 ? (row[colSentence] ?? "").trim() : "";
        const img = colImage >= 0 ? (row[colImage] ?? "").trim() : "";
        w.imageUrl = img || null;
        const order = colOrder >= 0 ? Number(row[colOrder]) : i;
        w.orderIndex = Number.isFinite(order) ? order : i;
        return w;
      })
      .filter((w): w is NonNullable<typeof w> => w != null);

    const nextLesson: CustomLesson = {
      ...base,
      id: bucket.meta.id || base.id,
      title: bucket.meta.title || base.title,
      theme,
      timeOfDay,
      words,
      questionsCount: Math.min(Math.max(1, words.length), 5),
      isCustom: true,
    };
    saveCustomLesson(nextLesson);
    if (prev) report.lessonsUpdated++;
    else report.lessonsCreated++;
    report.wordsImported += words.length;
  }
  return report;
}

/** Trigger a file download for the given CSV text. */
export function downloadCsv(text: string, filename = "word-runner-lessons.csv"): void {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay so Safari has time to read the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
